import isPromise from 'is-promise';
import { getIf } from '../data-set/utils';
function isPromiseCallback(task) {
    return typeof task === 'function';
}
class PromiseTask {
    constructor(task) {
        this[Symbol.toStringTag] = 'Promise';
        this.task = task;
        if (isPromise(task)) {
            this.promise = task;
        }
    }
    run() {
        const { promise } = this;
        if (promise) {
            return promise;
        }
        const { task } = this;
        if (isPromiseCallback(task)) {
            const newPromise = task();
            this.promise = newPromise;
            const { resolveCallbacks, rejectCallbacks } = this;
            if (resolveCallbacks && resolveCallbacks.length) {
                newPromise.then(v => {
                    resolveCallbacks.forEach(cb => cb(v));
                    delete this.resolveCallbacks;
                    return v;
                });
            }
            if (rejectCallbacks && rejectCallbacks.length) {
                newPromise.catch(v => {
                    rejectCallbacks.forEach(cb => cb(v));
                    delete this.rejectCallbacks;
                    throw v;
                });
            }
            return newPromise;
        }
        return task;
    }
    catch(onrejected) {
        const { promise } = this;
        if (promise) {
            return promise.catch(onrejected && (error => onrejected(error)));
        }
        return new Promise((_, reject) => {
            const rejectCallbacks = getIf(this, 'rejectCallbacks', []);
            rejectCallbacks.push((v) => reject(onrejected ? onrejected(v) : v));
        });
    }
    then(onfulfilled, onrejected) {
        const { promise } = this;
        if (promise) {
            return promise.then(onfulfilled && (value => onfulfilled(value)), onrejected && (error => onrejected(error)));
        }
        return new Promise((resolve, reject) => {
            const resolveCallbacks = getIf(this, 'resolveCallbacks', []);
            const rejectCallbacks = getIf(this, 'rejectCallbacks', []);
            resolveCallbacks.push((v) => resolve(onfulfilled ? onfulfilled(v) : v));
            rejectCallbacks.push((v) => reject(onrejected ? onrejected(v) : v));
        });
    }
}
export default class PromiseQueue {
    constructor(threads = 1) {
        this.queue = [];
        this.queueing = false;
        this.$nextTask = () => {
            const task = this.queue.shift();
            if (task) {
                return task.run().then(() => {
                    if (this.queueing) {
                        return this.$nextTask();
                    }
                }).catch(error => {
                    this.stop();
                    throw error;
                });
            }
            return Promise.resolve();
        };
        if (threads < 1) {
            throw new Error('The first argument of PromiseQueue constructor must be greater than 0.');
        }
        this.threads = threads;
    }
    $clear(promise) {
        if (!this.queueing) {
            const { queue } = this;
            const index = queue.indexOf(promise);
            if (index !== -1) {
                queue.splice(index, 1);
            }
        }
    }
    add(promise) {
        const { queue } = this;
        const task = new PromiseTask(promise);
        queue.push(task);
        return task
            .then(value => {
            this.$clear(task);
            return value;
        })
            .catch(error => {
            this.$clear(task);
            throw error;
        });
    }
    stop() {
        this.queueing = false;
    }
    async ready() {
        const { queue, threads } = this;
        if (queue.length) {
            this.queueing = true;
            try {
                await Promise.all(new Array(threads).fill(0).map(() => this.$nextTask()));
            }
            finally {
                this.queueing = false;
            }
        }
    }
}
